import type { ComponentInit } from "@scripts/registry";

const TARGET = "data-fa-countdown-to";
const STATE = "data-fa-countdown-state";
const UNIT = "[data-fa-countdown-unit]";
const VALUE = "[data-fa-countdown-value]";

/* Without an offset the string is parsed as local time, so one markup counts to a different
   instant in every timezone. Refuse it rather than count to the wrong one. */
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/i;

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const initCountdown: ComponentInit = (root) => {
	const raw = root.getAttribute(TARGET) ?? "";
	const target = Date.parse(raw);

	if (Number.isNaN(target) || !HAS_TIMEZONE.test(raw)) {
		console.warn(`[fa-ui] countdown: "${raw}" is not an ISO 8601 instant with a timezone`);
		return;
	}

	const values = new Map<string, HTMLElement>();
	// Restored on teardown, so a stale HTML snapshot never captures a computed time.
	const authored = new Map<HTMLElement, string>();

	for (const tile of root.querySelectorAll<HTMLElement>(UNIT)) {
		const unit = tile.dataset["faCountdownUnit"];
		const value = tile.querySelector<HTMLElement>(VALUE);

		if (!unit || !value) continue;

		values.set(unit, value);
		authored.set(value, value.textContent ?? "");
	}

	const listeners = new AbortController();
	let timer: number | undefined;
	let onScreen = false;

	const write = (unit: string, amount: number) => {
		const element = values.get(unit);
		const next = String(amount);

		if (element && element.textContent !== next) element.textContent = next;
	};

	const render = () => {
		const remaining = Math.max(0, target - Date.now());

		write("days", Math.floor(remaining / DAY));
		write("hours", Math.floor(remaining / HOUR) % 24);
		write("minutes", Math.floor(remaining / MINUTE) % 60);
		write("seconds", Math.floor(remaining / SECOND) % 60);

		root.setAttribute(STATE, remaining === 0 ? "elapsed" : "counting");

		return remaining;
	};

	const stop = () => {
		if (timer !== undefined) window.clearTimeout(timer);
		timer = undefined;
	};

	const tick = () => {
		const remaining = render();

		if (remaining === 0) return;

		// Re-aimed at the next whole second each time, so drift cannot accumulate.
		timer = window.setTimeout(tick, SECOND - (Date.now() % SECOND));
	};

	/* Nothing ticks until the countdown is on screen — a consumer that hides it gets no writes. */
	const sync = () => {
		stop();
		if (onScreen && !document.hidden) tick();
	};

	/* The parent, not the root: the not-ready rule hides the root, so watching it could never
	   see the first frame. A zero box means a consumer has hidden us. */
	const resize = new ResizeObserver((entries) => {
		const next = entries.some(
			({ contentRect }) => contentRect.width > 0 || contentRect.height > 0,
		);

		if (next === onScreen) return;

		onScreen = next;
		sync();
	});

	/* Rendered here, not from the gate: the not-ready rule hides the root, so a parent sized by
	   this child measures 0 and the gate would deadlock on its own hidden state. */
	render();

	document.addEventListener("visibilitychange", sync, { signal: listeners.signal });
	resize.observe(root.parentElement ?? root);

	return () => {
		listeners.abort();
		resize.disconnect();
		stop();
		root.removeAttribute(STATE);

		for (const [element, text] of authored) element.textContent = text;
	};
};
